import { useEffect } from 'react'
import type { Course } from '@mc/api'
import { useStaticData } from '@mc/static-data-react'
import { track } from '@mc/track-event'
import type { DynamicHeroValues } from './createServerDynamicHero'

type UTMContent = string | string[] | undefined

const objectHasAnyValues = <T>(
  obj: T | null | undefined,
): obj is NonNullable<T> => {
  if (!obj) return false
  if (typeof obj !== 'object') return false
  const values = Object.values(obj)
  return values.some((v) => typeof v === 'string' && v.length > 0)
}

type TrackViewedCMHeaderProps = {
  title: string
  subtitle: string
  nameplate: string
  trailerMediaUuid?: string
  isDynamicHero: boolean
  primaryCategory: number
}

type UtmKeyToPropertyNameMapType = {
  utmPropertyIndicator: string
  propertyName: string
}

const utmKeyToPropertyNameMap = [
  { utmPropertyIndicator: 'head_', propertyName: 'headerKey' },
  { utmPropertyIndicator: 'subhead_', propertyName: 'subheaderKey' },
  { utmPropertyIndicator: 'nameplate_', propertyName: 'nameplateKey' },
  {
    utmPropertyIndicator: 'trailerMediaUuid_',
    propertyName: 'trailerMediaUuidKey',
  },
]

const UTM_CONTENT_SEPARATOR = /\s|\+/

export const getDynamicValuesFromUtmContent = (utmContent: UTMContent) => {
  if (!utmContent || typeof utmContent !== 'string') return null
  const keys = utmContent.split(UTM_CONTENT_SEPARATOR)

  const {
    headerKey,
    subheaderKey,
    imageKey,
    mobileImageKey,
    nameplateKey,
    trailerMediaUuidKey,
  } = keys.reduce((acc: Record<string, string | undefined>, key) => {
    const item = utmKeyToPropertyNameMap.find((f) =>
      key.startsWith(f.utmPropertyIndicator),
    ) as UtmKeyToPropertyNameMapType
    if (!item) return acc
    acc[item.propertyName] = key.replace(item.utmPropertyIndicator, '')
    return acc
  }, {})

  return {
    headerKey,
    subheaderKey,
    imageKey,
    mobileImageKey,
    nameplateKey,
    trailerMediaUuidKey,
  }
}

const useCMDynamicHeroStaticData = () => {
  const { course, savedDynamicHero } = useStaticData<{
    course: Course
    savedDynamicHero: DynamicHeroValues
  }>()

  return { course, savedDynamicHero }
}

type UseCMDynamicHeroOpts = {
  /**
   * If true, then default values fetched from the class data will be returned
   * (preserves legacy behavior).
   *
   * If false, then dynamic hero values will be returned only if they are
   * present (in the UTM query string or in the saved dynamic CM cookie).
   * This makes it easier to use custom fallbacks.
   */
  useCourseDefaultValues?: boolean
}
export const useCMDynamicHero = (
  opts: UseCMDynamicHeroOpts = { useCourseDefaultValues: false },
) => {
  const { course, savedDynamicHero } = useCMDynamicHeroStaticData()

  const courseDefaultValues = {
    header: course?.headline,
    subheader: course?.marketingCmColumn,
    nameplate: course?.nameplate,
    trailerMediaUuid: course?.mediaUuid,
  }

  const resolvedValues = {
    ...(opts.useCourseDefaultValues ? courseDefaultValues : {}),
    ...savedDynamicHero,
  }
  const shouldDisplayDynamicHero = objectHasAnyValues(savedDynamicHero)

  useEffect(() => {
    if (shouldDisplayDynamicHero) {
      trackViewedCMHeader({
        title: resolvedValues?.header || '',
        subtitle: resolvedValues?.subheader || '',
        nameplate: resolvedValues?.nameplate || '',
        trailerMediaUuid: resolvedValues?.trailerMediaUuid || '',
        isDynamicHero: shouldDisplayDynamicHero,
        primaryCategory: course.primaryCategory?.id as number,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!shouldDisplayDynamicHero && !opts?.useCourseDefaultValues) {
    return {}
  }

  return resolvedValues
}

export const trackViewedCMHeader = ({
  title,
  subtitle,
  nameplate,
  trailerMediaUuid,
  isDynamicHero,
  primaryCategory,
}: TrackViewedCMHeaderProps) => {
  track('Viewed CM Header', {
    title,
    subtitle,
    nameplate,
    trailerMediaUuid,
    context: {
      dynamic_header: isDynamicHero,
    },
    p1: primaryCategory,
  })
}
